import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { VEHICLES, COMPANY_INFO } from '../constants';
import { API_EMAIL_URL } from '../api-config';
import { ArrowRight, CheckCircle2, Loader2, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const Booking: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const preselectedVehicleId = queryParams.get('vehicle');

  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(preselectedVehicleId || null);
  const [formData, setFormData] = useState({
    pickup: '', dropoff: '', date: '', time: '', name: '', email: '', phone: ''
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (preselectedVehicleId) setSelectedVehicle(preselectedVehicleId);
  }, [preselectedVehicleId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrorMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedVehicle || !formData.pickup || !formData.dropoff || !formData.date || !formData.time || !formData.name || !formData.email) {
      setErrorMessage('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setStatus('loading');

    try {
      const response = await fetch(API_EMAIL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'booking',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          vehicle: selectedVehicle,
          pickup: formData.pickup,
          dropoff: formData.dropoff,
          date: formData.date,
          time: formData.time
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMessage(data.message);
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Une erreur est survenue. Veuillez réessayer.');
    }
  };

  const formSectionVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  // Get vehicle name for display
  const getVehicleName = (id: string | null) => {
    if (!id) return '';
    const vehicles: Record<string, string> = {
      'class-s': 'Mercedes-Benz Classe S',
      'class-e': 'Mercedes-Benz Classe E',
      'class-v': 'Mercedes-Benz Classe V'
    };
    return vehicles[id] || id;
  };

  // Format date for display
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Format time for display
  const formatTime = (timeStr: string) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    return `${hours}h${minutes}`;
  };

  // Success page component
  const SuccessPage = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="space-y-8 bg-white p-8 md:p-12 shadow-xl shadow-slate-200/50 rounded-sm border border-slate-100 text-center"
    >
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-10 h-10 text-green-600" />
      </div>
      <h2 className="font-serif text-4xl md:text-5xl text-black mb-4">Demande envoyée !</h2>
      <p className="text-slate-600 text-lg leading-relaxed">
        Merci <strong>{formData.name}</strong> !<br />
        Nous avons bien reçu votre demande de réservation.
      </p>
      
      <div className="bg-slate-50 p-6 rounded-lg border border-slate-200 text-left">
        <p className="text-gold-600 font-bold uppercase tracking-widest text-xs mb-4">Récapitulatif</p>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-slate-400">Véhicule:</span>
            <p className="font-semibold text-slate-800">{getVehicleName(selectedVehicle)}</p>
          </div>
          <div>
            <span className="text-slate-400">Date & Heure:</span>
            <p className="font-semibold text-slate-800">{formatDate(formData.date)} à {formatTime(formData.time)}</p>
          </div>
          <div className="col-span-2">
            <span className="text-slate-400">Départ:</span>
            <p className="font-semibold text-slate-800">{formData.pickup}</p>
          </div>
          <div className="col-span-2">
            <span className="text-slate-400">Destination:</span>
            <p className="font-semibold text-slate-800">{formData.dropoff}</p>
          </div>
        </div>
      </div>
    
      
      <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
        <button 
          onClick={() => { setStatus('idle'); setFormData({ pickup: '', dropoff: '', date: '', time: '', name: '', email: '', phone: '' }); setSelectedVehicle(null); }}
          className="bg-black text-white px-8 py-3 font-bold uppercase tracking-widest hover:bg-gold-600 transition-colors"
        >
          Nouvelle réservation
        </button>
        <Link 
          to="/"
          className="bg-slate-200 text-slate-800 px-8 py-3 font-bold uppercase tracking-widest hover:bg-slate-300 transition-colors text-center"
        >
          Retour à l'accueil
        </Link>
      </div>
    </motion.div>
  );

  return (
    <div className="pt-40 pb-20 bg-slate-50 min-h-screen px-6 lg:px-12 text-slate-900">
      <div className="max-w-4xl mx-auto">
        <motion.div
           initial={{ opacity: 0, y: 50 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ duration: 0.8 }}
           className="mb-16"
        >
          <span className="text-gold-600 font-bold uppercase tracking-widest text-xs block mb-4">FORMULAIRE DE RÉSERVATION</span>
          <h1 className="font-serif text-5xl md:text-6xl text-black mb-4">Demande de Réservation</h1>
          <p className="text-slate-500 font-light text-lg">Complétez le formulaire ci-dessous.</p>
        </motion.div>

        {status === 'success' ? (
          <SuccessPage />
        ) : (
          <motion.form 
            onSubmit={handleSubmit}
            className="space-y-12 bg-white p-8 md:p-12 shadow-xl shadow-slate-200/50 rounded-sm border border-slate-100"
            initial="hidden"
            animate="visible"
            variants={{
               visible: { transition: { staggerChildren: 0.2 } }
            }}
          >
            
            {errorMessage && (
              <motion.div variants={formSectionVariants} className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg text-sm">
                {errorMessage}
              </motion.div>
            )}
            
            {/* Section 1: Itinerary */}
            <motion.div variants={formSectionVariants}>
               <div className="flex items-center gap-4 mb-8 border-b border-slate-100 pb-4">
                 <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center font-serif text-sm">1</div>
                 <h3 className="text-black text-lg font-bold uppercase tracking-widest">Itinéraire</h3>
               </div>
               
               <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                  <div className="group relative">
                     <label className="block text-slate-500 text-xs uppercase font-bold mb-2">Lieu de prise en charge *</label>
                     <input 
                        type="text" 
                        name="pickup" 
                        value={formData.pickup}
                        placeholder="Ex: Aéroport Genève, Hôtel Beau-Rivage..." 
                        className="w-full bg-slate-50 border-b-2 border-slate-200 py-4 px-4 text-slate-900 text-lg focus:outline-none focus:border-gold-500 transition-colors placeholder-slate-300"
                        onChange={handleChange}
                     />
                  </div>
                  <div className="group relative">
                     <label className="block text-slate-500 text-xs uppercase font-bold mb-2">Destination *</label>
                     <input 
                        type="text" 
                        name="dropoff" 
                        value={formData.dropoff}
                        placeholder="Ex: Lausanne Centre, Gstaad..." 
                        className="w-full bg-slate-50 border-b-2 border-slate-200 py-4 px-4 text-slate-900 text-lg focus:outline-none focus:border-gold-500 transition-colors placeholder-slate-300"
                        onChange={handleChange}
                     />
                  </div>
                  <div className="group relative">
                     <label className="block text-slate-500 text-xs uppercase font-bold mb-2">Date *</label>
                     <input 
                        type="date" 
                        name="date" 
                        value={formData.date}
                        className="w-full bg-slate-50 border-b-2 border-slate-200 py-4 px-4 text-slate-900 text-lg focus:outline-none focus:border-gold-500 transition-colors"
                        onChange={handleChange}
                     />
                  </div>
                  <div className="group relative">
                     <label className="block text-slate-500 text-xs uppercase font-bold mb-2">Heure *</label>
                     <input 
                        type="time" 
                        name="time" 
                        value={formData.time}
                        className="w-full bg-slate-50 border-b-2 border-slate-200 py-4 px-4 text-slate-900 text-lg focus:outline-none focus:border-gold-500 transition-colors"
                        onChange={handleChange}
                     />
                  </div>
               </div>
            </motion.div>

            {/* Section 2: Vehicle */}
            <motion.div variants={formSectionVariants}>
               <div className="flex items-center gap-4 mb-8 border-b border-slate-100 pb-4">
                 <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center font-serif text-sm">2</div>
                 <h3 className="text-black text-lg font-bold uppercase tracking-widest">Véhicule *</h3>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {VEHICLES.map((v) => (
                     <div 
                        key={v.id} 
                        onClick={() => setSelectedVehicle(v.id)}
                        className={`cursor-pointer p-6 transition-all border-2 relative ${selectedVehicle === v.id ? 'border-black bg-black text-white shadow-lg' : 'border-slate-100 bg-slate-50 text-slate-500 hover:border-slate-300'}`}
                     >
                        {selectedVehicle === v.id && <div className="absolute top-4 right-4 text-gold-500"><CheckCircle2 size={20} /></div>}
                        <div className="text-xl font-serif mb-2">{v.name}</div>
                        <div className={`text-xs uppercase tracking-wider ${selectedVehicle === v.id ? 'text-slate-400' : 'text-slate-400'}`}>Max {v.passengers} pax</div>
                     </div>
                  ))}
               </div>
            </motion.div>

            {/* Section 3: Contact */}
            <motion.div variants={formSectionVariants}>
               <div className="flex items-center gap-4 mb-8 border-b border-slate-100 pb-4">
                 <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center font-serif text-sm">3</div>
                 <h3 className="text-black text-lg font-bold uppercase tracking-widest">Coordonnées *</h3>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                  <div className="group">
                     <label className="block text-slate-500 text-xs uppercase font-bold mb-2">Nom *</label>
                     <input 
                        type="text" 
                        name="name" 
                        value={formData.name}
                        className="w-full bg-slate-50 border-b-2 border-slate-200 py-4 px-4 text-slate-900 text-lg focus:outline-none focus:border-gold-500 transition-colors"
                        onChange={handleChange}
                     />
                  </div>
                  <div className="group">
                     <label className="block text-slate-500 text-xs uppercase font-bold mb-2">Email *</label>
                     <input 
                        type="email" 
                        name="email" 
                        value={formData.email}
                        className="w-full bg-slate-50 border-b-2 border-slate-200 py-4 px-4 text-slate-900 text-lg focus:outline-none focus:border-gold-500 transition-colors"
                        onChange={handleChange}
                     />
                  </div>
                  <div className="group md:col-span-2">
                     <label className="block text-slate-500 text-xs uppercase font-bold mb-2">Téléphone</label>
                     <input 
                        type="tel" 
                        name="phone" 
                        value={formData.phone}
                        className="w-full bg-slate-50 border-b-2 border-slate-200 py-4 px-4 text-slate-900 text-lg focus:outline-none focus:border-gold-500 transition-colors"
                        onChange={handleChange}
                     />
                  </div>
               </div>
            </motion.div>

            <motion.div variants={formSectionVariants} className="pt-8">
               <button 
                  type="submit" 
                  disabled={status === 'loading'}
                  className="w-full md:w-auto bg-black text-white px-16 py-6 font-bold uppercase tracking-widest hover:bg-gold-600 transition-colors text-sm flex items-center justify-center gap-4 shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
               >
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="animate-spin" size={20} /> Traitement en cours...
                    </>
                  ) : (
                    <>Confirmer la demande <ArrowRight size={20} /></>
                  )}
               </button>
               <p className="mt-6 text-center text-slate-400 text-xs">En cliquant sur confirmer, vous acceptez d'être recontacté pour finaliser cette réservation.</p>
            </motion.div>

          </motion.form>
        )}
      </div>
    </div>
  );
};

export default Booking;
